/**
 * Event permission/state derivation, ported from the mobile app's
 * src/Screens/event-detail-updated/hooks/use-event-details-derived-state.js.
 *
 * This panel only serves the event owner or an assigned sub-promoter (never
 * a joining end-user), so the joiner-only flags mobile computes
 * (isUserAllowed, isUserJoined, isUserEventHost, assistant-permission gates)
 * are intentionally left out — see the approved plan's "Role model" section.
 */

const pad2 = (n) => String(n).padStart(2, "0");

/** Best-effort current-user id, defensive about which field this panel's stored user object uses. */
export const resolveCurrentUserId = (user) =>
  user?.UserId ?? user?.Id ?? user?.BusinessDetails?.UserId ?? null;

/**
 * The id to compare against Event.UserId for ownership/admin purposes. A
 * Sub-Account's own AppUser.Id never matches an event's UserId (backend
 * attributes Sub-Account-created events to the owning Business Account) — so
 * when acting as a Sub-Account, compare against the owner's id instead. Kept
 * separate from resolveCurrentUserId, which stays the literal logged-in
 * user's own id for identity-scoped uses (chat authorship, BA-invite
 * self-exclusion) where impersonating the owner would be wrong.
 */
export const resolveEffectiveOwnerId = (user, subAccountContext) =>
  subAccountContext?.IsSubAccount ? subAccountContext.OwnerUserId ?? null : resolveCurrentUserId(user);

export const deriveEventPermissions = (event, user, subAccountContext) => {
  const currentUserId = resolveEffectiveOwnerId(user, subAccountContext);
  const belongsToOwner = currentUserId != null && event?.UserId != null && String(currentUserId) === String(event.UserId);
  const hasPermission = (permission) => !subAccountContext?.IsSubAccount || Boolean(subAccountContext?.Permissions?.[permission]);
  const canManageEvents = belongsToOwner && hasPermission("CanManageEvents");
  const canManageAccess = belongsToOwner && hasPermission("CanManageAccessQrCheckIn");
  const canManageGallery = belongsToOwner && hasPermission("CanManageGallery");
  const canManageRooms = belongsToOwner && hasPermission("CanManageRooms");
  const canManageSingleMedia = belongsToOwner && hasPermission("CanManageSingleMedia");
  const canManagePricing = belongsToOwner && hasPermission("CanManagePricing");
  // A Sub-Account only gets admin/edit controls with CanManageEvents granted -
  // otherwise the backend would reject the write anyway (EventCrudService.
  // PostEventAsync/PutEventAsync), so hiding the controls here avoids
  // exposing actions that are guaranteed to fail.
  const isAdmin = canManageEvents;

  const isFamilyEvent = event?.EventType === "FamilyEvent";
  const isARoomEvent = event?.EventType === "ARoom";

  const chatRoomUsers = event?.ChatRoom?.ChatRoomUsers ?? [];
  const adminChatRoomUser = chatRoomUsers.find((u) => u?.UserId === event?.UserId) ?? null;
  const adminBusinessDetails = adminChatRoomUser?.AppUser?.BusinessDetails ?? null;
  const origin = event?.Origin;

  const isPublicEvent = Boolean(adminBusinessDetails) && origin === "Public";
  const isNonPublicEvent = Boolean(adminBusinessDetails) && origin !== "Public";
  const isPrivateEvent = adminBusinessDetails === null && origin === "Private";

  const isIndividualQR = event?.QRCodeType === true;

  // Chat media approval (the Settings toggle, the Pending Approval queue, and
  // MessageBubble's inline blurred approve/reject preview) is scoped to
  // NonPublic "Others"-category events only — every other event type keeps
  // these controls hidden rather than showing them non-functional. Origin/
  // EventType are the same raw fields CreateEvent.jsx writes (Origin:
  // 'Public' | 'NonPublic', EventType holding the category string, e.g.
  // 'Others', for a NonPublic event) — checked directly rather than via
  // isNonPublicEvent above, which additionally requires an admin
  // ChatRoomUser with BusinessDetails and isn't the same condition.
  const isMediaApprovalEligible = event?.Origin === "NonPublic" && event?.EventType === "Others";

  const startMs = event?.StartDateTime ? new Date(event.StartDateTime).getTime() : null;
  const endMs = event?.PromtorEventEndDateTime ? new Date(event.PromtorEventEndDateTime).getTime() : null;
  const now = Date.now();

  const isEventStarted = startMs != null && now - startMs > 0;
  const isEventEnded = endMs != null && now - endMs > 0;
  const isEventPassed = endMs != null && now - endMs > 30 * 60 * 1000; // 30 min after end

  return {
    isAdmin,
    belongsToOwner,
    canManageEvents,
    canManageAccess,
    canManageGallery,
    canManageRooms,
    canManageSingleMedia,
    canManagePricing,
    isFamilyEvent,
    isARoomEvent,
    isPublicEvent,
    isPrivateEvent,
    isNonPublicEvent,
    isMediaApprovalEligible,
    isIndividualQR,
    isEventStarted,
    isEventEnded,
    isEventPassed,
    adminChatRoomUser,
  };
};

/**
 * Chat-window status, ported from mobile's getChatStatus() in both
 * event-detail-updated/index.js and details-tab/event-summary.js.
 * Chat opens 30 minutes before start, closes at end + OpenDoorDays days.
 */
export const getChatStatus = (event) => {
  if (!event?.StartDateTime) return { isChatEnabled: false, chatStatus: "" };

  const start = new Date(event.StartDateTime);
  const end = new Date(event.PromtorEventEndDateTime || event.StartDateTime);
  const openDoorDays = parseInt(event.OpenDoorDays, 10) || 0;
  const disableAt = new Date(end.getTime() + openDoorDays * 24 * 60 * 60 * 1000);
  const chatStart = new Date(start.getTime() - 30 * 60 * 1000);
  const now = new Date();

  if (now < chatStart) {
    return { isChatEnabled: false, chatStatus: `Chat opens ${chatStart.toLocaleString()}` };
  }
  if (now <= disableAt) {
    const remainingMs = disableAt.getTime() - now.getTime();
    const days = Math.floor(remainingMs / (24 * 60 * 60 * 1000));
    const hours = Math.floor((remainingMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000));
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (parts.length === 0) parts.push(`${minutes}m`);
    return { isChatEnabled: true, chatStatus: `Chat closes in ${parts.join(" ")}` };
  }
  return { isChatEnabled: false, chatStatus: "Chat ended" };
};

/** Event status badge (Upcoming / Live / Ended), used by EventHeader. */
export const getEventStatusBadge = (event) => {
  // An @Room's deactivated state (Event.IsClosed) takes priority over the
  // date-based status below — a closed Room isn't "Live" just because its
  // dates say so.
  if (event?.IsClosed) return { label: "Deactivated", color: "#6b7280", bg: "#f3f4f6" };

  const startMs = event?.StartDateTime ? new Date(event.StartDateTime).getTime() : null;
  const endMs = event?.PromtorEventEndDateTime ? new Date(event.PromtorEventEndDateTime).getTime() : null;
  const now = Date.now();

  if (endMs != null && now > endMs) return { label: "Ended", color: "#6b7280", bg: "#f3f4f6" };
  if (startMs != null && now > startMs) return { label: "Live", color: "#16a34a", bg: "#dcfce7" };
  return { label: "Upcoming", color: "#6c63ff", bg: "#ede9ff" };
};

export const formatEventDateRange = (event) => {
  if (!event?.StartDateTime) return "";
  const start = new Date(event.StartDateTime);
  const dateStr = start.toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" });
  const weekdayStr = start.toLocaleDateString(undefined, { weekday: "long" });
  const startTimeStr = `${pad2(start.getHours())}:${pad2(start.getMinutes())}`;
  if (!event?.PromtorEventEndDateTime) return `${dateStr} — ${weekdayStr}, ${startTimeStr}`;
  const end = new Date(event.PromtorEventEndDateTime);
  const endTimeStr = `${pad2(end.getHours())}:${pad2(end.getMinutes())}`;
  return `${dateStr} — ${weekdayStr}, ${startTimeStr} - ${endTimeStr}`;
};
